import { appPageBoilerplateFetch } from '../functions/pageBoilerplate.js'
import { fetchSystemStatus, fetchUptime } from '../functions/system/fetchStatus.js'

const ENVS = ['prod', 'staging', 'testing', 'dev', 'upcoming', 'next']

const _cell = (root, repo, env) => {
  const m = root?.systemMatrix || {}
  return m[`${repo}:${env}`] || { version: '—', status: 'none', commit: '—', message: '', url: '' }
}

const STATUS = {
  healthy: { color: 'green', label: 'Healthy' },
  building: { color: 'orange', label: 'Building' },
  failed: { color: 'red', label: 'Failed' },
  pending: { color: 'caption', label: 'Pending' },
  none: { color: 'gray', label: 'No data' }
}

const _dotColor = (status) => (STATUS[status] || STATUS.none).color

// ── Tooltip system ──

let _tooltip = null

const _ensureTooltip = () => {
  if (_tooltip) return _tooltip
  _tooltip = document.createElement('div')
  _tooltip.style.cssText = `
    position:fixed;z-index:9999;pointer-events:none;
    padding:8px 12px;border-radius:6px;
    background:var(--color-overlayStrong, #1a1a1e);
    border:1px solid var(--color-line, #333);
    box-shadow:0 8px 24px -4px rgba(0,0,0,0.5);
    opacity:0;transition:opacity 120ms ease;
    max-width:320px;font-size:12px;line-height:1.4;
  `
  document.body.appendChild(_tooltip)
  return _tooltip
}

const _showTooltip = (anchor, content) => {
  const tip = _ensureTooltip()
  tip.innerHTML = content
  tip.style.opacity = '1'
  const rect = anchor.getBoundingClientRect()
  const tipW = tip.offsetWidth
  let x = rect.left + rect.width / 2 - tipW / 2
  let y = rect.bottom + 6
  if (x < 8) x = 8
  if (x + tipW > window.innerWidth - 8) x = window.innerWidth - tipW - 8
  if (y + tip.offsetHeight > window.innerHeight - 8) y = rect.top - tip.offsetHeight - 6
  tip.style.left = x + 'px'
  tip.style.top = y + 'px'
}

const _hideTooltip = () => {
  if (_tooltip) _tooltip.style.opacity = '0'
}

const _statusIcon = (status) => {
  const s = STATUS[status] || STATUS.none
  return `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:var(--color-${s.color});vertical-align:middle;margin-right:4px;"></span>`
}

const _tooltipHtml = (repo, env, c) => {
  const s = STATUS[c.status] || STATUS.none
  let html = `<div style="margin-bottom:4px;font-weight:700;color:var(--color-title);">${repo} <span style="color:var(--color-caption);font-weight:400;">/ ${env}</span></div>`
  html += `<div style="margin-bottom:4px;">${_statusIcon(c.status)}<span style="color:var(--color-${s.color});font-weight:600;">${s.label}</span></div>`
  if (c.version && c.version !== '—') html += `<div style="color:var(--color-caption);">Version: <span style="color:var(--color-title);font-weight:500;">${c.version}</span></div>`
  if (c.commit && c.commit !== '—') html += `<div style="color:var(--color-caption);">Commit: <code style="color:var(--color-blue);font-size:11px;">${c.commit}</code></div>`
  if (c.message) {
    const msgColor = c.status === 'failed' ? 'var(--color-red)' : 'var(--color-caption)'
    html += `<div style="margin-top:4px;padding-top:4px;border-top:1px solid var(--color-line);color:${msgColor};font-size:11px;">${c.message.replace(/</g, '&lt;')}</div>`
  }
  return html
}

// ── Grid builder ──

const _buildGrid = (el, s, repos, title) => {
  if (!el.node) return
  el.node.innerHTML = ''
  const root = s.root

  const wrapper = document.createElement('div')
  wrapper.style.cssText = 'padding:16px;'

  const h2 = document.createElement('h2')
  h2.style.cssText = 'margin:0 0 12px 0;font-size:18px;font-weight:800;color:var(--color-title);letter-spacing:-0.01em;'
  h2.textContent = title
  wrapper.appendChild(h2)

  const grid = document.createElement('div')
  grid.style.cssText = `display:grid;grid-template-columns:minmax(120px,auto) repeat(${ENVS.length},minmax(140px,1fr));`

  const hdrStyle = 'padding:10px 16px;border-bottom:2px solid var(--color-line);font-size:10px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--color-caption);'
  const cellStyle = 'padding:8px 12px;border-bottom:1px solid var(--color-line);text-align:center;'
  const repoStyle = 'padding:8px 16px;border-bottom:1px solid var(--color-line);font-weight:600;font-size:13px;color:var(--color-title);white-space:nowrap;display:flex;align-items:center;'

  const corner = document.createElement('div')
  corner.style.cssText = hdrStyle + 'text-align:left;'
  corner.textContent = title === 'Packages' ? 'Package' : 'Service'
  grid.appendChild(corner)

  for (const env of ENVS) {
    const th = document.createElement('div')
    th.style.cssText = hdrStyle + 'text-align:center;'
    th.textContent = env
    grid.appendChild(th)
  }

  for (const repo of repos) {
    const label = document.createElement('div')
    label.style.cssText = repoStyle
    label.textContent = repo
    grid.appendChild(label)

    for (const env of ENVS) {
      const c = _cell(root, repo, env)
      const td = document.createElement('div')
      td.style.cssText = cellStyle + 'display:flex;flex-direction:column;align-items:center;gap:2px;position:relative;cursor:default;'

      // Enhanced tooltip on hover
      td.onmouseenter = () => _showTooltip(td, _tooltipHtml(repo, env, c))
      td.onmouseleave = _hideTooltip

      const dot = document.createElement('span')
      dot.style.cssText = `width:6px;height:6px;border-radius:50%;background:var(--color-${_dotColor(c.status)});`

      const verEl = document.createElement('span')
      verEl.style.cssText = 'font-size:10px;font-weight:600;color:var(--color-title);'
      verEl.textContent = c.version

      const commitEl = document.createElement(c.url ? 'a' : 'code')
      commitEl.style.cssText = `font-size:9px;font-family:monospace;color:var(--color-caption);${c.url ? 'text-decoration:none;cursor:pointer;' : ''}`
      commitEl.textContent = c.commit
      if (c.url) {
        commitEl.href = c.url
        commitEl.target = '_blank'
        commitEl.onmouseenter = () => { commitEl.style.color = 'var(--color-blue)'; commitEl.style.textDecoration = 'underline' }
        commitEl.onmouseleave = () => { commitEl.style.color = 'var(--color-caption)'; commitEl.style.textDecoration = 'none' }
      }

      td.appendChild(dot)
      td.appendChild(verEl)
      td.appendChild(commitEl)
      grid.appendChild(td)
    }
  }

  wrapper.appendChild(grid)
  el.node.appendChild(wrapper)
}

// Infrastructure list derived from systemUptime data at render time — no static list

const _doFetch = async (s) => {
  s.root.update({ systemLoading: true })
  try {
    const [data, uptime] = await Promise.all([
      fetchSystemStatus(),
      fetchUptime()
    ])
    s.root.update({
      systemMatrix: data.matrix || {},
      systemNpm: data.npm || {},
      systemServices: data.services || [],
      systemPackages: data.packages || [],
      systemUptime: uptime || {},
      systemLoading: false
    })
  } catch (err) {
    console.error('[system] fetch failed:', err)
    s.root.update({ systemLoading: false })
  }
}

const _matrixCard = {
  round: 'Z2',
  background: 'overlay',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'line',
  overflow: 'visible',
  minHeight: 'fit-content'
}

export const system = {
  extends: 'AppPage',
  metadata: { title: 'System / Symbols Workspace' },
  fetch: appPageBoilerplateFetch,
  onCreate: (el, s) => _doFetch(s),

  SystemMain: {
    extends: 'AppMain',
    padding: '0',
    Header: {
      extends: 'PageHeader',
      LeftGroup: { Title: { text: 'System' } },
      RightGroup: {
        RefreshBtn: {
          tag: 'button', type: 'button', text: 'Refresh',
          padding: 'Z A', round: 'Z',
          borderWidth: '1px', borderColor: 'line', borderStyle: 'solid',
          background: 'overlay', cursor: 'pointer', fontSize: 'Z2', fontWeight: '500', color: 'title',
          ':hover': { background: 'overlayStrong' },
          onClick: (e, el, s) => _doFetch(s)
        }
      }
    },

    Body: {
      overflow: 'auto', padding: 'B', flow: 'y', gap: 'B',
      maxWidth: 'J2', margin: '0 auto', width: '100%',

      Hero: {
        flow: 'y', gap: 'Z', padding: '0 Z',
        Eyebrow: {
          tag: 'span', fontSize: 'Z2', fontWeight: '700', letterSpacing: '0.12em', textTransform: 'uppercase',
          color: (el, s) => s.root?.systemLoading ? 'orange' : 'green',
          text: (el, s) => s.root?.systemLoading ? 'Fetching status...' : 'All systems operational'
        },
        Title: {
          tag: 'h1', fontSize: 'E', fontWeight: '800', color: 'title',
          letterSpacing: '-0.03em', lineHeight: '1.1', margin: '0', text: 'System Status'
        }
      },

      Legend: {
        flow: 'x', gap: 'B', align: 'center left', padding: 'Z',
        childrenAs: 'state',
        children: () => Object.entries(STATUS).map(([key, v]) => ({ key, ...v })),
        childExtends: {
          flow: 'x', align: 'center left', gap: 'Y',
          Dot: { width: 'X2', height: 'X2', round: '50%', background: (el, s) => s.color },
          Label: { tag: 'span', fontSize: 'Y2', color: 'caption', text: (el, s) => s.label }
        }
      },

      ServicesMatrix: {
        ..._matrixCard,
        onRender: (el, s) => {
          const build = () => {
            const repos = (s.root?.systemServices?.length) ? s.root.systemServices : ['canvas', 'preview', 'intranet', 'server', 'mermaid', 'mcp-https', 'platform', 'landing', 'docs']
            _buildGrid(el, s, repos, 'Services')
          }
          build()
          el._interval = setInterval(() => {
            const k = Object.keys(s.root?.systemMatrix || {}).length
            if (k !== el._prevK) { el._prevK = k; build() }
          }, 500)
        },
        onRemove: (el) => { if (el._interval) clearInterval(el._interval) }
      },

      PackagesMatrix: {
        ..._matrixCard,
        onRender: (el, s) => {
          const build = () => {
            const repos = (s.root?.systemPackages?.length) ? s.root.systemPackages : ['smbls', 'sdk', 'mcp-local', 'vscode', 'chrome']
            _buildGrid(el, s, repos, 'Packages')
          }
          build()
          el._interval = setInterval(() => {
            const k = Object.keys(s.root?.systemMatrix || {}).length
            if (k !== el._prevK) { el._prevK = k; build() }
          }, 500)
        },
        onRemove: (el) => { if (el._interval) clearInterval(el._interval) }
      },

      InfraCard: {
        flow: 'y', padding: 'B', round: 'Z2',
        background: 'overlay', borderWidth: '1px', borderStyle: 'solid', borderColor: 'line',

        InfraTitle: {
          flow: 'x', align: 'center space-between', margin: '0 0 A 0',
          H2: { tag: 'h2', fontSize: 'B1', fontWeight: '800', color: 'title', margin: '0', text: 'Infrastructure' },
          Period: { tag: 'span', fontSize: 'Y2', color: 'caption', text: '120 days' }
        },

        InfraRows: {
          flow: 'y',
          onRender: (el, s) => {
            const build = () => {
              if (!el.node) return
              el.node.innerHTML = ''
              const uptimeData = s.root?.systemUptime || {}
              const serviceNames = Object.keys(uptimeData)
              if (!serviceNames.length) return

              for (const svcName of serviceNames) {
                const svc = { name: svcName }
                const row = document.createElement('div')
                row.style.cssText = 'display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid var(--color-line);'

                // Left: dot + name
                const left = document.createElement('div')
                left.style.cssText = 'display:flex;align-items:center;gap:6px;min-width:200px;width:200px;'
                const days = uptimeData[svc.name] || []
                const latest = days.length ? days[days.length - 1] : null
                const currentStatus = latest?.status || 'healthy'
                const dot = document.createElement('span')
                dot.style.cssText = `width:8px;height:8px;border-radius:50%;flex-shrink:0;background:var(--color-${currentStatus === 'healthy' ? 'green' : currentStatus === 'degraded' ? 'orange' : 'red'});`
                const name = document.createElement('span')
                name.style.cssText = 'font-size:12px;color:var(--color-title);'
                name.textContent = svc.name
                left.appendChild(dot)
                left.appendChild(name)

                // Center: 120-day bar chart — bars flex to fill available space
                const bars = document.createElement('div')
                bars.style.cssText = 'flex:1;display:flex;align-items:center;gap:1px;justify-content:center;'
                const dayCount = 120
                let healthyCount = 0

                for (let i = 0; i < dayCount; i++) {
                  const day = days[i] || { status: 'healthy', checked_at: '', message: '' }
                  if (day.status === 'healthy') healthyCount++
                  const bar = document.createElement('span')
                  const c = day.status === 'healthy' ? 'green' : day.status === 'degraded' ? 'orange' : 'red'
                  bar.style.cssText = `flex:1;max-width:6px;min-width:2px;height:24px;border-radius:1px;background:var(--color-${c});opacity:0.85;cursor:default;transition:opacity 100ms, transform 100ms;`
                  bar.onmouseenter = () => {
                    bar.style.opacity = '1'
                    bar.style.transform = 'scaleY(1.3)'
                    const dateStr = day.checked_at || `${dayCount - i} days ago`
                    const statusLabel = day.status === 'healthy' ? 'Operational' : day.status === 'degraded' ? 'Degraded' : 'Down'
                    let html = `<div style="font-weight:700;color:var(--color-title);margin-bottom:2px;">${svc.name}</div>`
                    html += `<div style="color:var(--color-caption);font-size:11px;">${dateStr}</div>`
                    html += `<div>${_statusIcon(day.status === 'healthy' ? 'healthy' : day.status === 'degraded' ? 'building' : 'failed')}<span style="color:var(--color-${c});font-weight:600;">${statusLabel}</span></div>`
                    if (day.message) html += `<div style="margin-top:3px;font-size:11px;color:var(--color-${c});">${day.message}</div>`
                    if (day.latency_ms) html += `<div style="font-size:11px;color:var(--color-caption);">Latency: ${day.latency_ms}ms</div>`
                    _showTooltip(bar, html)
                  }
                  bar.onmouseleave = () => {
                    bar.style.opacity = '0.85'
                    bar.style.transform = 'scaleY(1)'
                    _hideTooltip()
                  }
                  bars.appendChild(bar)
                }

                // Right: uptime percentage
                const pct = dayCount > 0 ? ((healthyCount / dayCount) * 100).toFixed(2) + '%' : svc.uptime
                const uptimeEl = document.createElement('span')
                uptimeEl.style.cssText = `font-size:12px;font-weight:600;color:var(--color-green);min-width:55px;text-align:right;`
                uptimeEl.textContent = pct

                row.appendChild(left)
                row.appendChild(bars)
                row.appendChild(uptimeEl)
                el.node.appendChild(row)
              }
            }
            build()
            el._interval = setInterval(() => {
              const k = Object.keys(s.root?.systemUptime || {}).length
              if (k !== el._prevK) { el._prevK = k; build() }
            }, 500)
          },
          onRemove: (el) => { if (el._interval) clearInterval(el._interval) }
        }
      }
    }
  }
}
